import React, { useState } from "react";
import { useForm } from "react-hook-form";
import clsx from "clsx";
import { CircleLoader } from "@/components/ui/circleLoader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { setNewPassword } from "@/views/auth/api";
import * as Sentry from "@sentry/react";

interface FormInputs {
  password: string;
  confirm_password: string;
}

const passwordFieldValidator = {
  required: "This is required.",
  minLength: {
    value: 12,
    message: "Password must be at least 12 characters long.",
  },
};

interface SetNewPasswordFormProps {
  token: string;
}

export const SetNewPasswordForm: React.FC<SetNewPasswordFormProps> = ({ token }) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setError] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>();

  const onSubmit = async (payload: FormInputs) => {
    try {
      setError(false);
      await setNewPassword({ ...payload, secret_token: token });
      setIsSuccess(true);
    } catch (error) {
      setError(true);
      Sentry.captureException(error);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} method="post" autoComplete="off">
      {isSuccess && (
        <Alert variant="success">
          <AlertDescription>
            New password has been set successfully. You can now sign in with your new password.
          </AlertDescription>
        </Alert>
      )}
      {isError && (
        <Alert variant="destructive">
          <AlertDescription>Reset Link Expired</AlertDescription>
        </Alert>
      )}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-ui-paragraph-text">
          Password
        </label>
        <div className="mt-1">
          <input
            id="password"
            type="password"
            required
            {...register("password", passwordFieldValidator)}
            className={clsx(
              "block w-full appearance-none rounded-md border border-input px-3 py-2 placeholder-muted shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm",
              {
                "border-red-500": !!errors.password,
              },
            )}
          />
          {!!errors.password && <p className="mt-2 text-sm text-red-500">{errors.password.message}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="confirm_password" className="block text-sm font-medium text-ui-paragraph-text">
          Confirm password
        </label>
        <div className="mt-1">
          <input
            id="confirm_password"
            type="password"
            required
            {...register("confirm_password", {
              ...passwordFieldValidator,
              validate: confirmPasswordValidation,
            })}
            className={clsx(
              "block w-full appearance-none rounded-md border border-input px-3 py-2 placeholder-muted shadow-sm focus:border-primary focus:outline-none focus:ring-primary sm:text-sm",
              {
                "border-red-500": !!errors.confirm_password,
              },
            )}
          />
          {!!errors.confirm_password && <p className="mt-2 text-sm text-red-500">{errors.confirm_password.message}</p>}
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting && <CircleLoader />}
          {isSubmitting ? "Loading..." : "Set new password"}
        </button>
      </div>
    </form>
  );
};

const confirmPasswordValidation = (value: string, formValues: FormInputs) =>
  value === formValues.password || "Passwords do not match";
